import React from 'react';
import { formatCurrency } from '../../../utils/formatting';
import type { OrderSummaryData } from '../../../types';
import type { DiscountBreakdown, InstallmentInfo } from '../../../offer.types';

interface OrderSummaryFooterProps {
  summary: OrderSummaryData;
  finalTotal: number;
  discountBreakdown?: DiscountBreakdown;
  installmentInfo?: InstallmentInfo;
}

export const OrderSummaryFooter: React.FC<OrderSummaryFooterProps> = ({ 
  summary, 
  finalTotal,
  discountBreakdown,
  installmentInfo
}) => {
  const couponApplied = (summary.couponCode ?? '').trim().length > 0;

  const getPaymentMethodDiscountLabel = (paymentMethod?: string): string => {
    switch (paymentMethod) {
      case 'PIX':
        return 'Desconto no PIX:';
      case 'CARD':
        return 'Desconto no Cartão:';
      case 'BOLETO':
        return 'Desconto no Boleto:';
      default:
        return 'Desconto Método de Pagamento:';
    }
  };


  return (
    <div className="space-y-2 p-4 border rounded border-gray-200 dark:border-gray-700">
      <div className="flex justify-between text-sm text-text-secondary dark:text-gray-400">
        <span>Subtotal:</span>
        <span>{formatCurrency(discountBreakdown?.basePrice ?? summary.subtotal ?? 0)}</span>
      </div>
      
      {/* Descontos fragmentados ou desconto único */}
      {discountBreakdown ? (
        <>
          {(discountBreakdown.progressiveDiscount ?? 0) > 0 && (
            <div className="flex justify-between font-bold items-center text-sm text-red-600">
              <div className="flex items-center gap-2">
                <span>Desconto Progressivo:</span>
              </div>
              <span>
                -R$ {discountBreakdown.progressiveDiscount!.toFixed(2).replace('.', ',')}
              </span>
            </div>
          )}
          {discountBreakdown.couponDiscount > 0 && (
            <div className="flex justify-between font-bold items-center text-sm text-red-600">
              <div className="flex items-center gap-2">
                <span title={summary.couponCode}>Desconto Cupom:</span>
              </div>
              <span title={summary.coupons[0]?.description || summary.couponCode}>
                -R$ {discountBreakdown.couponDiscount.toFixed(2).replace('.', ',')}
              </span>
            </div>
          )}
          {discountBreakdown.paymentMethodDiscount > 0 && (
            <div className="flex justify-between font-bold items-center text-sm text-red-600">
              <div className="flex items-center gap-2">
                <span>{getPaymentMethodDiscountLabel(discountBreakdown.paymentMethod)}</span>
              </div>
              <span>
                -R$ {discountBreakdown.paymentMethodDiscount.toFixed(2).replace('.', ',')}
              </span>
            </div>
          )}
        </>
      ) : (
        summary.couponCode && couponApplied && summary.discountAmount && summary.discountAmount > 0 && (
          <div className="flex justify-between font-bold items-center text-sm text-red-600">
            <div className="flex items-center gap-2">
              <span title={summary.couponCode}>Desconto da Oferta:</span>
            </div>
            <span title={summary.coupons[0]?.description || summary.couponCode}>
              -R$ {summary.discountAmount.toFixed(2).replace('.', ',')}
            </span>
          </div>
        )
      )}

      {/* Oferta Especial (order bump) — exibida após os descontos, deixando claro que não é descontada */}
      {discountBreakdown && (discountBreakdown.orderBumpTotal ?? 0) > 0 && (
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700 mt-1">
          <div className="flex justify-between items-center text-sm font-medium text-text-primary dark:text-gray-100">
            <span>Oferta Especial:</span>
            <span>+ R$ {discountBreakdown.orderBumpTotal!.toFixed(2).replace('.', ',')}</span>
          </div>
        </div>
      )}

      <div className="flex justify-between text-lg font-bold text-text-primary dark:text-gray-100 pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
        <span>Valor Total:</span>
        <div className="text-right">
          {installmentInfo ? (
            <>
              {installmentInfo.installments > 1 ? (
                // Cartão - Mostrar parcelamento
                <>
                  <div className="text-green-600">
                    {installmentInfo.installments}x R$ {installmentInfo.installment_amount.toFixed(2).replace('.', ',')}
                  </div>
                  <div className="text-xs text-gray-500">
                    (ou R$ {installmentInfo.cash_price.toFixed(2).replace('.', ',')} à vista)
                  </div>
                </>
              ) : (
                // PIX/Boleto - Mostrar apenas valor à vista
                <div className="text-green-600">
                  R$ {installmentInfo.cash_price.toFixed(2).replace('.', ',')}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-green-600">
                12x R$ {(finalTotal / 12).toFixed(2).replace('.', ',')}
              </div>
              <div className="text-xs text-gray-500">
                (ou R$ {finalTotal.toFixed(2).replace('.', ',')} à vista)
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 